import type { BoundingBox } from "@/types";

const JPEG_QUALITY = 0.92;
const BBOX_COORD_MAX = 1000;

// Applies a 90° clockwise rotation to a bounding box defined in the
// 0..BBOX_COORD_MAX viewBox space used throughout the AI pipeline.
// Mirrors the canvas rotation performed by `rotateImage`.
export function rotateBoundingBox90CW(bbox: BoundingBox): BoundingBox {
  return {
    xMin: BBOX_COORD_MAX - bbox.yMax,
    yMin: bbox.xMin,
    xMax: BBOX_COORD_MAX - bbox.yMin,
    yMax: bbox.xMax,
  };
}

export function rotateBoundingBox90CCW(bbox: BoundingBox): BoundingBox {
  return {
    xMin: bbox.yMin,
    yMin: BBOX_COORD_MAX - bbox.xMax,
    xMax: bbox.yMax,
    yMax: BBOX_COORD_MAX - bbox.xMin,
  };
}

export function rotateBoundingBoxNTimes(bbox: BoundingBox, n: number): BoundingBox {
  const times = ((n % 4) + 4) % 4;
  let result = bbox;
  for (let i = 0; i < times; i += 1) {
    result = rotateBoundingBox90CW(result);
  }
  return result;
}

export function rotateImage(file: File, direction: "cw" | "ccw" = "cw"): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      const canvas = document.createElement("canvas");
      canvas.width = img.height;
      canvas.height = img.width;

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Could not get canvas context"));
        return;
      }

      if (direction === "cw") {
        ctx.translate(canvas.width, 0);
        ctx.rotate(Math.PI / 2);
      } else {
        ctx.translate(0, canvas.height);
        ctx.rotate(-Math.PI / 2);
      }
      ctx.drawImage(img, 0, 0);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("Could not create blob"));
            return;
          }
          const rotated = new File(
            [blob],
            file.name.replace(/\.\w+$/, ".jpg"),
            { type: "image/jpeg" }
          );
          resolve(rotated);
        },
        "image/jpeg",
        JPEG_QUALITY
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not load image"));
    };

    img.src = url;
  });
}
